import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEdit } from "@fortawesome/free-solid-svg-icons";
import Switch from "react-input-switch";
import UploadDoc from "../../../assests/upload.png";
import Document_show from "../../../assests/Document_show.png";
import { Api } from "../../../services/httpService";
import { url, subscriptionKey } from "../../../constants";
import Loader from "../Loader";
import LoaderModal from "../LoaderModal";
import { getFilePreview } from "../../../utils/utils";
const DocumentUpload = (props) => {
  const [uploadedFiles, setUploadedFiles] = useState(props.uploadedFiles || []);
  const [showPreview, setShowPreview] = useState(false);
  const [currentPreview, setCurrentPreview] = useState("");
  const [previewType, setPreviewType] = useState("");
  const [loading, setLoading] = useState(false);
  const [filename, setFileName] = useState("");
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  const [filePassword, setFilePassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isRevealingPassword, setIsRevealingPassword] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isPasswordEditable, setIsPasswordEditable] = useState(true);

  // Handle file upload (limit to 1 file)
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const validTypes = ["application/pdf", "image/png", "image/jpeg"];
    let filtered;
    if (props.type === "bankStatement") {
      filtered = files.filter(
        (file) =>
          validTypes.includes(file.type) && file.size <= 50 * 1024 * 1024
      );
    } else {
      filtered = files.filter(
        (file) => validTypes.includes(file.type) && file.size <= 15 * 1024 * 1024
      );
    }

    // Only allow 1 file
    const newFiles = filtered.slice(0, 1).map((file) => ({
      name: file.name,
      date: new Date().toISOString(),
      fileObject: file,
      isServerFile: false,
    }));
    setUploadedFiles(newFiles);
    if (props.setUploadedFiles) {
      props.setUploadedFiles(newFiles);
    }
    e.target.value = null;
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploadedFiles.length < 1) {
      const files = Array.from(e.dataTransfer.files);
      const validTypes = ["application/pdf", "image/png", "image/jpeg"];
      const filtered = files.filter(
        (file) => validTypes.includes(file.type) && file.size <= 15 * 1024 * 1024
      );
      const newFiles = filtered.slice(0, 1).map((file) => ({
        name: file.name,
        date: new Date().toISOString(),
        fileObject: file,
        isServerFile: false,
      }));
      setUploadedFiles(newFiles);
      if (props.setUploadedFiles) {
        props.setUploadedFiles(newFiles);
      }
    }
  };

  // Remove file handler
  const handleRemoveFileLocal = (file) => {
    // Remove from local state
    const updatedFiles = uploadedFiles.filter((f) => f !== file);
    setUploadedFiles(updatedFiles);
    if (props.setUploadedFiles) {
      props.setUploadedFiles(updatedFiles);
    }
    // If it's a server file, call parent handler to trigger API
    if (file.isServerFile && props.handleRemoveFile) {
      props.handleRemoveFile(file, props.type);
    }
  };

  // Preview handler for both local and server files
  const handlePreview = async (file) => {
    if (file) {
      setShowPreview(true);
      setPreviewType("");
      setCurrentPreview("");
      if (file?.fileId) {
        setLoading(true);
      }
      try {
        const { success, preview } = await getFilePreview(file);
        if (success) {
          setFileName(preview?.name);
          setPreviewType("application/pdf");
          setCurrentPreview(preview?.url);
        } else {
          setCurrentPreview("");
          alert("Unable to load preview.");
        }
      } catch (err) {
        setCurrentPreview("");
        alert("Unable to load preview.");
      } finally {
        setLoading(false);
      }
    }
  };

  const closePreview = () => {
    if (currentPreview && !previewType.startsWith("http")) {
      URL.revokeObjectURL(currentPreview);
    }
    setShowPreview(false);
    setCurrentPreview("");
    setPreviewType("");
    setLoading(false);
  };

  const hasLocalFiles = uploadedFiles.some((file) => !file.isServerFile);
  const hasServerFile = uploadedFiles.some((file) => file.isServerFile);
  const hasProtectedServerFile = uploadedFiles.some(
    (file) => file.isServerFile && file.isPasswordProtected
  );
  const canUpdatePasswordOnly =
    props.type === "bankStatement" &&
    hasServerFile &&
    (!isPasswordProtected || filePassword.trim());

  useEffect(() => {
    if (!props.documentModal) return;
    setUploadedFiles(props.uploadedFiles || []);
    const hasProtected = (props.uploadedFiles || []).some(
      (file) => file.isServerFile && file.isPasswordProtected
    );
    setIsPasswordProtected(hasProtected);
    setIsPasswordVisible(false);
    setIsPasswordEditable(!hasProtected);
    if (!hasProtected) {
      setFilePassword("");
      setPasswordError("");
    }
  }, [props.documentModal, props.uploadedFiles]);

  const handleRevealPassword = async () => {
    const protectedFile = uploadedFiles.find(
      (file) => file.isServerFile && file.isPasswordProtected
    );
    if (!protectedFile?.docId) return;
    setIsRevealingPassword(true);
    try {
      const response = await Api(
        "get",
        `/documents/revealDocumentPassword/${protectedFile.docId}`
      );
      const revealedPassword = response?.body?.password;
      if (response?.success && revealedPassword) {
        setIsPasswordProtected(true);
        setFilePassword(revealedPassword.trim());
        setPasswordError("");
        setIsPasswordVisible(true);
      } else {
        alert("Unable to reveal password.");
      }
    } catch (err) {
      console.error(err);
      alert("Unable to reveal password.");
    } finally {
      setIsRevealingPassword(false);
    }
  };

  // Submit handler
  const handleSubmit = async () => {
    try {
      if (
        props.type === "bankStatement" &&
        isPasswordProtected &&
        !filePassword.trim()
      ) {
        setPasswordError("Password is required.");
        return;
      }
      if (props.type === "bankStatement" && !hasLocalFiles) {
        const targetDoc = uploadedFiles.find((file) => file.isServerFile);
        if (!targetDoc?.docId) return;
        setLoading(true);
        const payload = {
          docId: targetDoc.docId,
          isPasswordProtected: !!isPasswordProtected,
          ...(isPasswordProtected ? { docPassword: filePassword.trim() } : {}),
        };
        const response = await Api(
          "put",
          `/documents/updateDocumentPassword`,
          payload
        );
        setLoading(false);
        if (response?.success) {
          props.setUploadSuccess(true);
          setIsPasswordEditable(!isPasswordProtected);
          props.fetchBankDetail();
          props.setDocumentModal(false);
        } else {
          alert(response?.message || "Unable to update password.");
        }
        return;
      }
      setLoading(true);
      const formData = new FormData();
      if (props.type === "bankStatement") {
        formData.append("documentInfoId", 5);
      } else if (props.type === "cancelledCheque") {
        formData.append("documentInfoId", 10);
      }
      if (props.type === "bankStatement") {
        formData.append(
          "isPasswordProtected",
          isPasswordProtected ? "true" : "false"
        );
        if (isPasswordProtected) {
          formData.append("docPassword", filePassword);
        }
      }
      formData.append("entityType", "COMPANY");
      formData.append("entityId", props?.entityId);
      formData.append("merchantId", props?.merchantId);
      formData.append("bankAccountId", props?.bankAccountId);
      formData.append("docCountry", "UAE");
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const formattedDate = `${yyyy}-${mm}-${dd}`;
      formData.append("docIssueDate", formattedDate);
      formData.append("docExpiryDate", "");
      uploadedFiles
        .filter((file) => file.fileObject)
        .forEach((file) => {
          formData.append("files", file.fileObject);
        });
      const response = await Api(
        "post",
        `/documents/uploadOrUpdateDocuments`,
        formData,
        true
      );
      setLoading(false);
      if (response.success && response.body) {
        props.setUploadSuccess(true);
        setUploadedFiles([]);
        setIsPasswordProtected(false);
        setFilePassword("");
        setPasswordError("");
        props.fetchBankDetail();
        props.setDocumentModal(false);
      }
    } catch (err) {
      setLoading(false);
      console.error(err);
    }
  };

  const getDaysAgoText = (input) => {
    // Handle both Date objects and date strings
    const uploadedDate = input instanceof Date ? input : new Date(input);
    if (isNaN(uploadedDate)) return "Invalid date";

    const now = new Date();
    const timeDiff = now - uploadedDate;
    const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

    if (daysDiff === 0) return "Uploaded today";
    if (daysDiff > 0)
      return `Uploaded ${daysDiff} day${daysDiff > 1 ? "s" : ""} ago`;
    return `Uploaded in ${Math.abs(daysDiff)} day${
      Math.abs(daysDiff) > 1 ? "s" : ""
    }`;
  };
  return (
    <>
      {/* Upload Modal */}
      <Modal
        show={props.documentModal}
        onHide={() => {
          // Clear only locally uploaded files (isServerFile: false)
          const serverFiles = uploadedFiles.filter((f) => f.isServerFile);
          setUploadedFiles(serverFiles);
          if (props.setUploadedFiles) {
            props.setUploadedFiles(serverFiles);
          }
          setIsPasswordProtected(false);
          setFilePassword("");
          setPasswordError("");
          props.setDocumentModal(false);
        }}
        backdrop="static"
        className="Upload-trade-license"
        keyboard={false}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header
          closeButton
          className="d-flex justify-content-between py-3 px-4"
        >
          <Modal.Title>{props.title}</Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">
          {props.guideLinesContent}
          {loading || props.loadingModal ? (
            <div className="col-12 text-center">
              <LoaderModal />
            </div>
          ) : (
            <>
              {uploadedFiles.map((file, index) => (
                <div
                  key={file._k || `${file.name}-${index}`}
                  className="file-upload-white-box d-flex align-items-start justify-content-between p-3 mb-3 bg-white"
                >
                  <div className="d-flex align-items-start flex-grow-1">
                    <div className="me-3">
                      <div className="load-img-box rounded-circle p-1 d-flex align-items-center justify-content-center">
                        <img src={Document_show} alt="Document Icon" />
                      </div>
                    </div>
                    <div className="file-upload-box">
                      <div
                        className="file-title text-truncate mb-1"
                        style={{ maxWidth: "200px" }}
                        title={file.name}
                      >
                        {file.name}
                      </div>
                      <div className="file-sub-title text-muted small">
                        {getDaysAgoText(file.date ? file.date : Date.now())}
                      </div>
                      <div
                        className="file-preview mt-2 text-primary"
                        style={{ cursor: "pointer" }}
                        onClick={() => handlePreview(file)}
                      >
                        Tap to Preview
                      </div>
                    </div>
                  </div>
                  <button
                    className="btn btn-link text-danger p-0"
                    onClick={() => handleRemoveFileLocal(file)}
                    title="Delete"
                    aria-label="Delete file"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="bi bi-trash3"
                      viewBox="0 0 16 16"
                    >
                      <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47M8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5" />
                    </svg>
                  </button>
                </div>
              ))}

              {uploadedFiles.length < 1 && (
                <div
                  className="license-upload-section d-flex flex-column align-items-center justify-content-center p-4 mt-4"
                  style={{
                    borderStyle: "dashed",
                    cursor:
                      uploadedFiles.length >= 1 ? "not-allowed" : "pointer",
                    opacity: uploadedFiles.length >= 1 ? 0.5 : 1,
                  }}
                  onClick={() => {
                    if (uploadedFiles.length < 1) {
                      document.getElementById("fileUpload").click();
                    }
                  }}
                  role="button"
                  tabIndex="0"
                  onKeyDown={(e) =>
                    e.key === "Enter" && uploadedFiles.length < 1
                      ? document.getElementById("fileUpload").click()
                      : null
                  }
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={handleDrop}
                >
                  <img src={UploadDoc} alt="Upload Icon" />
                  <p className="mb-1 mt-2">Upload Document</p>
                  <small>
                    {" "}
                    {props.type === "cancelledCheque"
                      ? "(PDF JPG and PNG supported)"
                      : "(PDF supported)"}
                  </small>
                  <input
                    id="fileUpload"
                    type="file"
                    accept={
                      props.type === "cancelledCheque"
                        ? ".pdf, .png, .jpg"
                        : ".pdf"
                    }
                    multiple={false}
                    hidden
                    onChange={handleFileUpload}
                    disabled={uploadedFiles.length >= 1}
                  />
                </div>
              )}
            </>
          )}
          {props.type === "bankStatement" && (
            <div className="mb-3">
              <div className="d-flex justify-content-between">
                <div className="d-flex align-items-center">
                  <label className="mb-0 me-3">
                    Is this file password protected?
                  </label>
                  <Switch
                    className="switch-input-size"
                    value={isPasswordProtected ? 1 : 0}
                    onChange={(val) => {
                      const nextValue = Number(val) === 1;
                      setIsPasswordProtected(nextValue);
                      setPasswordError("");
                      if (!nextValue) {
                        setFilePassword("");
                        setIsPasswordVisible(false);
                        setIsPasswordEditable(true);
                      }
                      if (nextValue && hasProtectedServerFile) {
                        setIsPasswordEditable(false);
                      }
                    }}
                  />
                  <span className="ms-2">
                    {isPasswordProtected ? "Yes" : "No"}
                  </span>
                  {hasProtectedServerFile && (
                    <button
                      type="button"
                      className="btn btn-link p-0 ms-2"
                      onClick={handleRevealPassword}
                      title="Reveal password"
                      aria-label="Reveal password"
                      disabled={isRevealingPassword}
                    >
                      <FontAwesomeIcon icon={faEye} />
                    </button>
                  )}
                </div>
                <div>
                  {isPasswordProtected && (
                    <button
                      type="button"
                      className="p-0 ms-2"
                      onClick={() => setIsPasswordEditable(true)}
                      disabled={!isPasswordVisible}
                      title="Edit password"
                      aria-label="Edit password"
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-2">
                <input
                  type={isPasswordVisible ? "text" : "password"}
                  className="form-control"
                  placeholder={
                    isPasswordProtected ? "Password Hidden" : "Enter password"
                  }
                  value={filePassword}
                  disabled={!isPasswordProtected || !isPasswordEditable}
                  onChange={(e) => {
                    setFilePassword(e.target.value);
                    if (passwordError) setPasswordError("");
                  }}
                />
                {passwordError && (
                  <small className="text-danger">{passwordError}</small>
                )}
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="p-3 border-0">
          <Button
            variant={
              hasLocalFiles || canUpdatePasswordOnly
                ? "primary active"
                : "primary"
            }
            // variant={ uploadedFiles.length >0 ?
            //    "primary active" : "primary"

            // }
            disabled={
              loading ||
              (!hasLocalFiles && !canUpdatePasswordOnly) ||
              (props.type === "bankStatement" &&
                isPasswordProtected &&
                !filePassword.trim())
            }
            onClick={(e) => {
              if (!hasLocalFiles && !canUpdatePasswordOnly) {
                e.preventDefault();
                return;
              }
              handleSubmit();
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Preview Modal */}
      <Modal
        className="Upload-trade-license"
        show={showPreview}
        onHide={closePreview}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="justify-content-between">
          <Modal.Title className="text-truncate" style={{ maxWidth: "500px" }}>
            {filename || "Preview"}
          </Modal.Title>
        </Modal.Header>
        {loading ? (
          <div style={{ minHeight: "200px" }}>
            <LoaderModal />
          </div>
        ) : (
          <Modal.Body className="text-center">
            <div className="p-5 bg-light">
              {previewType.startsWith("image/") ? (
                <img
                  src={currentPreview}
                  alt="Preview"
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              ) : (
                <iframe
                  src={currentPreview}
                  title="Document Preview"
                  width="100%"
                  height="500px"
                  frameBorder="0"
                ></iframe>
              )}
            </div>
          </Modal.Body>
        )}
      </Modal>
    </>
  );
};

export default DocumentUpload;
